<template>
  <div v-if="selfContain" ref="selfContain" :class="{ 'date-field--opens-above': opensAbove }">
    <BaseInputField
      ref="baseInputField"
      :placeholder="$props.placeholder"
      v-bind="$props"
      :icon="hideIcons ? null : DateIcon"
      :model-value="parsedDate"
      :readonly="true"
      :show-arrow="true"
    />
  </div>
  <BaseInputField
    v-else
    ref="baseInputField"
    :placeholder="$props.placeholder"
    v-bind="$props"
    :icon="hideIcons ? null : DateIcon"
    :model-value="parsedDate"
    :readonly="true"
    :show-arrow="true"
  />
</template>

<script>
import DateIcon from '@tagvenue/icons/24/line/time/calendar.svg';
import throttle from 'lodash/throttle';
import { markRaw } from 'vue';

import htmlProps from '../htmlProps.js';
import BaseInputField from './BaseInputField.vue';
import commonProps from './common-props-mixin';
import validation from './validation-mixin';

export default {
  name: 'DateField',
  emits: ['change', 'update:modelValue'],
  mixins: [validation, commonProps, htmlProps],
  components: { BaseInputField },
  props: {
    fullyBookedDates: {
      type: Array,
      default() {
        return [];
      },
    },
    closedDaysIds: {
      type: Array,
      default() {
        return [];
      },
    },
    partiallyBookedDates: {
      type: Object,
      default() {
        return {};
      },
    },
    weekStart: {
      type: Number,
      default: 1,
    },
    uiDateFormat: {
      type: String,
      default: 'DD/MM/YYYY',
    },
    hideClearDateButton: {
      type: Boolean,
      default: false,
    },
    // eslint-disable-next-line vue/require-default-prop
    modelValue: {
      type: [String, Number],
    },
    isInModal: {
      type: Boolean,
      default: false,
    },
    modalWrapperSelector: {
      type: String,
      default: '.ds-modal-wrapper',
    },
    noticeHours: {
      type: Number,
      default: 0,
    },
    getContainer: {
      type: Function,
      default: null,
    },
    selfContain: {
      type: Boolean,
      default: false,
    },
    pastDatesEnabled: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: Date,
      default: null,
    },
    hideIcons: {
      type: Boolean,
      default: false,
    },
    /**
     * Only meaningful together with `selfContain`, where the calendar is positioned inside this
     * component rather than by pikaday itself.
     */
    opensAbove: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      DateIcon: markRaw(DateIcon),
    };
  },

  computed: {
    parsedDate() {
      if (!this.modelValue) {
        return '';
      }
      return window.dayjs(this.modelValue, 'DD/MM/YYYY').format(this.uiDateFormat);
    },
    scrollingContainer() {
      if (this.isInModal) {
        return document.querySelector(this.modalWrapperSelector || window);
      }
      return window;
    },
  },
  watch: {
    minDate(value) {
      if (this.pikaday) {
        this.pikaday.setMinDate(value);
        this.pikaday.gotoDate(value);
      }
    },
  },
  methods: {
    convertDateFormat(date) {
      return window.dayjs(date, 'YYYY-MM-DD').format('DD/MM/YYYY');
    },
    changeDate(date) {
      this.$emit('change', date);
      this.$emit('update:modelValue', date);
    },
    hide: throttle(function hide() {
      this.pikaday.hide();
    }, 100),
    assertDayJsPluginsLoaded() {
      if (window.dayjs('25-12-1995', 'DD-MM-YYYY').isValid() === false) {
        throw new Error('You cannot use DateField component without required dayjs plugins');
      }
    },
    getPikaContainer() {
      if (this.getContainer) {
        return this.getContainer();
      }

      if (this.selfContain) {
        return this.$refs.selfContain;
      }

      return null;
    },
  },

  mounted() {
    this.assertDayJsPluginsLoaded();
    this.pikaday = App.es6.shared.pikaday({
      defaultDate: this.modelValue,
      element: this.$refs.baseInputField.$el.querySelector('input'),
      container: this.getPikaContainer(),
      onSelect: this.changeDate,
      weekStart: this.weekStart,
      uiDateFormat: this.uiDateFormat,
      noticeHours: this.noticeHours,
      keyboardInput: false,
      closedDaysOfWeek: this.closedDaysIds,
      highlightedDates: Object.keys(this.partiallyBookedDates).map(this.convertDateFormat),
      fullyBookedDates: Array.isArray(this.fullyBookedDates)
        ? this.fullyBookedDates.map(this.convertDateFormat)
        : [],
      hideClearDateButton: this.hideClearDateButton,
      pastDatesEnabled: this.pastDatesEnabled,
      minDate: this.minDate,
    });

    this.scrollingContainer.addEventListener('scroll', this.hide);
    this.scrollingContainer.addEventListener('touchmove', this.hide);
  },

  beforeUnmount() {
    this.pikaday?.destroy();

    this.scrollingContainer.removeEventListener('scroll', this.hide);
    this.scrollingContainer.removeEventListener('touchmove', this.hide);
  },
};
</script>

<style lang="scss">
  .pika-row .is-outside-current-month.has-event button {
    background: initial;
  }

  /*
    Pikaday skips its own positioning whenever it renders into a container, so a self-contained
    calendar lands right below the input with no offsets of its own. Anchoring it to the top edge
    of the field opens it upwards, which keeps it from overflowing a short scroll container and
    shifting the layout as a scrollbar appears.
  */
  .date-field--opens-above {
    position: relative;
  }

  .date-field--opens-above .pika-single {
    bottom: 100%;
    left: 0;
    margin-bottom: 4px;
  }
</style>
